import { namespace } from 'wsm-tagging-utils';

const runInjector = (win) => {
	namespace(win, 'DDC.tracking.framework.config');

	const {
		DDC: { tracking },
		com
	} = win;

	tracking.framework.config.Injector = function Injector() {
		this.log = new com.dealer.log.Logger('ws-tagging:Injector');
	};

	tracking.framework.config.Injector.prototype = {
		/**
		 * The default constructor
		 */
		constructor: tracking.framework.config.Injector,

		/**
		 * Capitalizes the specified string value.
		 *
		 * @param {string} value The value to capitalize
		 * @return {string} The capitalized value
		 */
		capitalize(value) {
			return value.charAt(0).toUpperCase() + value.substring(1);
		},

		/**
		 * De-capitalizes the specified string value.
		 *
		 * @param {string} value The value to de-capitalize
		 * @return {string} The de-capitalized value
		 */
		decapitalize(value) {
			return value.charAt(0).toLowerCase() + value.substring(1);
		},

		/**
		 * Returns the setter or mutator function for the named property.
		 *
		 * The specified property is capitalized and prefixed with 'set', i.e.
		 * the property firstName will be transformed to 'setFirstName'
		 *
		 * @param {Object} object The object whose setter function is to be returned
		 * @param {string} property The name of the property being examined
		 * @return {Function} The setter for the named property; undefined if not present
		 */
		setterFor(object, property) {
			const setterName = `set${this.capitalize(property)}`;
			return object[setterName];
		},

		/**
		 * Injects the properties from the specified configuration into the specified object.
		 *
		 * This method will first attempt to find a setter function for each property defined
		 * in the configuration; if not found the property will be set directly on the object.
		 *
		 * @param {Object} object The object to be property injected
		 * @param {Object} configuration The source of property names and associated values
		 * @return {Object} The supplied object being property injected
		 */
		setProperties(object, configuration) {
			const newObject = object;

			Object.entries(configuration).forEach(([key, value]) => {
				const setter = this.setterFor(object, key);
				if (setter !== undefined) {
					if (this.log.isDebugEnabled()) {
						this.log.debug(
							`using setter injection to set ${key} to: ${JSON.stringify(
								value
							)}`
						);
					}
					setter.call(object, value);
				} else {
					if (this.log.isDebugEnabled()) {
						this.log.debug(
							`using field injection to set ${key} to: ${JSON.stringify(
								value
							)}`
						);
					}
					newObject[key] = value;
				}
			});
			return newObject;
		},

		/**
		 * Attempts to initialize the specified object by invoked the named
		 * function as the object initializer. If no function name is provided,
		 * the default value is 'initialize'.
		 *
		 * @param {Object} object The object being initialized
		 * @param {string|undefined} initializer The optional name of the initializer function;
		 *                 defaults to 'initialize'
		 * @param {boolean|undefined} required true to raise an error if the supplied object does
		 *                    not contain the initializer function; defaults to false
		 * @return {Object} The initialized object
		 */
		initialize(object, initializer = 'initialize', required) {
			if (
				object[initializer] !== undefined &&
				typeof object[initializer] === 'function'
			) {
				this.log.debug(`invoking ${initializer}()`);
				object[initializer].call(object);
			} else if (required) {
				throw new Error(`Initializer not found: ${initializer}()`);
			} else {
				this.log.debug(`initializer not found: ${initializer}()`);
			}
			return object;
		}
	};

	/**
	 * @type tracking.framework.config.Injector
	 */
	tracking.framework.config.Injector.singleton =
		new tracking.framework.config.Injector();

	/**
	 * Returns the single instance of the Injector.
	 *
	 * @return {tracking.framework.config.Injector}
	 * @see https://sourcemaking.com/design_patterns/singleton
	 */
	tracking.framework.config.Injector.get = function get() {
		return tracking.framework.config.Injector.singleton;
	};
};

export default runInjector;
